function CohablistPrivacy() {
  return (
    <div style={{ maxWidth: 720, margin: '60px auto', padding: '0 24px 80px', fontFamily: 'inherit', lineHeight: 1.7, color: '#222' }}>
      <h1 style={{ fontSize: '1.6rem', marginBottom: 4 }}>Cohablist — Privacy Policy</h1>
      <p style={{ color: '#666', marginTop: 0, marginBottom: 32 }}>Last updated: August 2026</p>

      <p>
        Cohablist ("the App") is a private household management tool for small groups of people sharing a home.
        This policy explains what data is collected and how it is used.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li><strong>Account email address</strong> — used to identify your account. Collected when you sign in with Google or Apple.</li>
        <li><strong>Display name</strong> — a name you choose yourself when joining a group. Used to identify you to your housemates.</li>
        <li><strong>Household data</strong> — shopping lists, chores, and events that you and your housemates create inside the App.</li>
        <li><strong>Subscription status</strong> — if you purchase Cohablist Premium, we store whether your subscription is active, its plan (monthly/yearly), and its renewal date. We do not receive or store your payment card details — these are handled entirely by Apple, Google, or RevenueCat (see "Payment processing" below).</li>
        <li><strong>Usage analytics</strong> — we record which in-app actions you take (e.g. creating a list, completing a chore, purchasing Premium) to understand how the App is used and to fix bugs. See "Analytics and diagnostics" below.</li>
      </ul>

      <h2>How we use it</h2>
      <p>Your data is used solely to operate the App:</p>
      <ul>
        <li>Your email identifies your account and lets you sign back in.</li>
        <li>Your display name is shown to your housemates inside the group.</li>
        <li>Household data (lists, chores, events) is shared in real time with the other members of your group, and only them.</li>
        <li>Subscription status determines which features are unlocked for your household.</li>
      </ul>
      <p>We do not sell or share your data with third parties for advertising, and we do not run any ad or ad-tracking SDKs in the App.</p>

      <h2>Analytics and diagnostics</h2>
      <p>
        We collect first-party usage analytics (e.g. which features are used, how often) and crash/error reports,
        tied to your account, to understand how the App is used and to diagnose problems. This data stays within
        our own systems — it is never sold, shared with advertisers, or used to serve ads, and it is not shared
        with any third-party analytics or advertising network.
      </p>

      <h2>Payment processing</h2>
      <p>
        Cohablist Premium subscriptions are purchased through Apple's App Store or Google's Play Store, and
        managed using <a href="https://www.revenuecat.com" target="_blank" rel="noopener noreferrer">RevenueCat</a>,
        a subscription management platform. When you subscribe, Apple/Google processes your payment directly — we
        never see or store your card details. RevenueCat receives your purchase and subscription status from
        Apple/Google in order to tell our systems whether your subscription is active. See RevenueCat's privacy
        policy at&nbsp;
        <a href="https://www.revenuecat.com/privacy" target="_blank" rel="noopener noreferrer">revenuecat.com/privacy</a>,
        Apple's at&nbsp;
        <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer">apple.com/legal/privacy</a>,
        and Google's at&nbsp;
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a>.
      </p>

      <h2>How it is stored</h2>
      <p>
        All data is stored on <a href="https://supabase.com" target="_blank" rel="noopener noreferrer">Supabase</a>,
        a cloud database platform. Supabase's privacy policy applies to the underlying infrastructure:&nbsp;
        <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">supabase.com/privacy</a>.
      </p>

      <h2>Data sharing</h2>
      <p>
        Your household data is visible to the other members of your group — people you have explicitly invited or
        whose invite code you have entered. It is not visible to anyone outside your group. We share data with
        Apple, Google, and RevenueCat only as needed to process and validate Premium subscription purchases (see
        "Payment processing" above), and with Supabase as our data hosting provider (see "How it is stored" above).
        We do not otherwise share your data with third parties.
      </p>

      <h2>Your rights</h2>
      <p>
        Depending on where you live, you may have rights under data protection law (such as the UK/EU GDPR or US
        state privacy laws) to access, correct, export, or delete your personal data, or to object to or restrict
        certain processing. To exercise any of these rights, contact us at the email below — see "Deleting your
        data" for how account deletion works in practice.
      </p>

      <h2>Deleting your data</h2>
      <p>
        To delete your data, leave your group from the account menu in the App, then contact us at the email below
        and we will remove your account and all associated data within 30 days.
      </p>

      <h2>Children</h2>
      <p>The App is not intended for use by children under 13. We do not knowingly collect data from children.</p>

      <h2>Changes</h2>
      <p>
        If this policy changes, the "Last updated" date above will be updated. Continued use of the App after a
        change constitutes acceptance of the new policy.
      </p>

      <h2>Contact</h2>
      <p>
        Questions about this policy:&nbsp;
        <a href="mailto:dennis.hvel@gmail.com">dennis.hvel@gmail.com</a>
      </p>
    </div>
  );
}

export default CohablistPrivacy;
